import React, { useState } from "react";
import { Button, Col, Row, Table } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useGetMyOutletsQuery } from "../slices/OutletSlice";
import { TiTick } from "react-icons/ti";
import Message from "../components/Message";
import Loader from "../components/Loader";
import { Link, useNavigate } from "react-router-dom";
import OutletDetailsScreen from "./OutletDetailsScreen";
import { useGetMyOutletFoodItemsQuery } from "../slices/FoodItemSilce";
import { useSelectOutletMutation } from "../slices/AdminSilce";
import { toast } from "react-toastify";
import { setCredentials } from "../slices/authSlice";
import ReUsableModal from "../components/ReUsableModal";
import { clearOrderedItems } from "../slices/orderedItemSlice";
import { apiSlice } from "../slices/apiSlice";

const ViewOutletsScreen = () => {
  const { userInfo } = useSelector((state) => state.auth);
  const orderItems = useSelector((state) => state.orderedItem.items);
  const navigate = useNavigate();
  const [selectedOutletId, setSelectedOutletId] = useState(null);
  const [showOutletModal, setShowOutletModal] = useState(false);
  const [pendingOutletId, setPendingOutletId] = useState(null);
  const [isConfirmedSwitch, setIsConfirmedSwitch] = useState(false);
  const {
    data: outlets,
    isLoading,
    error,
    refetch,
  } = useGetMyOutletsQuery({
    token: userInfo?.token,
  });
  const dispatch = useDispatch();
  const [changeOutlet] = useSelectOutletMutation();

  const handleSelect = async (outletId) => {
    try {
      if (
        outletId !== pendingOutletId &&
        outletId !== userInfo?.selectedOutletId &&
        orderItems.length > 0
      ) {
        setPendingOutletId(outletId);
        setShowOutletModal(true);
        return;
      }

      await changeOutlet({
        token: userInfo?.token,
        selectedOutletId: outletId,
      }).unwrap();

      // The switch only persisted selectedOutletId server-side — it did not
      // return fresh outlet details. Any outletId data already in redux may
      // be stale (edited from another session/tab since login), and every
      // other screen's cached queries (food items, orders, tables, ...) key
      // off the token alone, not the outlet, so they never refetch on a
      // switch by themselves. Clear the whole API cache so everything
      // refetches against the newly selected outlet, then pull a fresh
      // outlets list to replace what's stored in redux.
      dispatch(apiSlice.util.resetApiState());
      const freshOutlets = await refetch();

      const updatedUserInfo = structuredClone(userInfo);
      updatedUserInfo.selectedOutletId = outletId;
      if (freshOutlets?.data) {
        updatedUserInfo.outletId = freshOutlets.data;
      }

      dispatch(setCredentials(updatedUserInfo));
    } catch (err) {
      console.error("Error selecting outlet:", err);
      toast.error(
        err?.data?.error || "Error selecting outlet. Please try again."
      );
    }
  };

  const handleConfirmSwitch = async () => {
    dispatch(clearOrderedItems());
    setIsConfirmedSwitch(true);
    await handleSelect(pendingOutletId);
    setIsConfirmedSwitch(false);
    setShowOutletModal(false);
    setPendingOutletId(null);
  };

  const handleCancelSwitch = () => {
    setPendingOutletId(null);
    setShowOutletModal(false);
  };

  return (
    <>
      <Row>
        <Col>
          <Row className="mt-1">
            <label>View Outlets</label>
            <Col>
              <ReUsableModal
                title="You have items in cart. Do you want to discard them before switching?"
                size="md"
                payload={[]}
                show={showOutletModal}
                onSubmit={handleConfirmSwitch}
                onHide={handleCancelSwitch}
                submit="Yes"
                cancel="No"
              />
              {isLoading ? (
                <Loader />
              ) : error ? (
                <Message>
                  {error?.data?.error || "Error fetching outlets"}
                </Message>
              ) : (
                <Table
                  striped
                  bordered
                  hover
                  responsive="sm"
                  className="table-custom"
                >
                  <thead className={`${userInfo.themeColor}ThemeThead`}>
                    <tr>
                      <th>Outlet Name</th>
                      <th> </th>
                      <th>City</th>
                      <th>Contact Info</th>
                    </tr>
                  </thead>
                  <tbody>
                    {outlets?.length > 0 ? (
                      outlets.map((outlet) => (
                        <tr key={outlet._id}>
                          <td>
                            <a
                              onClick={() =>
                                navigate(`/outletDetails/${outlet._id}`)
                              }
                              style={{
                                cursor: "pointer",
                                textDecoration: "underline",
                              }}
                            >
                              {outlet?.outletName}
                            </a>
                            {userInfo?.selectedOutletId === outlet._id && (
                              <>
                                {" "}
                                <TiTick
                                  style={{
                                    color: "green",
                                    width: "1.5rem",
                                    height: "1.5rem",
                                  }}
                                />
                                (Currenly Selected)
                              </>
                            )}
                          </td>
                          <td>
                            {userInfo?.selectedOutletId !== outlet._id && (
                              <Button onClick={() => handleSelect(outlet._id)}>
                                Select This Outlet
                              </Button>
                            )}
                          </td>

                          <td>{outlet?.companyId?.companyCity || "N/A"}</td>
                          <td>{outlet?.outletContact || "N/A"}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={3}>
                          <Message>No outlets found.</Message>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              )}
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
};

export default ViewOutletsScreen;
